import { Loading } from '@everlutionsk/ui';
import { Box, Stack, Typography } from '@mui/material';
import ChartDeferred from 'chartjs-plugin-deferred';
import React, { useEffect, useState } from 'react';
import type { Doughnut } from 'react-chartjs-2';

// Lazy-loaded doughnut chart mirroring the pattern used in
// `packages/frontend/src/components/charts/doughnutChart.tsx` but
// self-contained — no dependency on the heavier ChartWrapper / InfoLabel
// / FooterProps machinery that lives in frontend/. Used by the admin
// Statistics tab to visualise asset-type and party breakdowns.

export interface DoughnutSlice {
  readonly label: string;
  readonly value: number;
  readonly color: string;
}

interface AdminDoughnutChartProps {
  readonly title: string;
  readonly description?: string;
  readonly slices: readonly DoughnutSlice[];
  readonly height?: number;
  readonly totalLabel?: string;
  readonly emptyMessage?: string;
}

// Module-level cache so we only register the chart.js plugins once across
// every mount of every chart on the page.
let chartReady: Promise<typeof Doughnut> | null = null;
function loadDoughnut(): Promise<typeof Doughnut> {
  if (chartReady != null) return chartReady;
  chartReady = Promise.all([
    import('react-chartjs-2'),
    import('chart.js'),
    import('chartjs-plugin-datalabels')
  ]).then(([{ Doughnut }, { ArcElement, Chart, Legend, Tooltip }, ChartDataLabels]) => {
    Chart.register(ArcElement, Tooltip, Legend, ChartDataLabels.default, ChartDeferred);
    return Doughnut;
  });
  return chartReady;
}

export function AdminDoughnutChart({
  title,
  description,
  slices,
  height = 280,
  totalLabel = 'Total',
  emptyMessage = 'No data to display.'
}: AdminDoughnutChartProps) {
  const [DoughnutCmp, setDoughnutCmp] = useState<typeof Doughnut | null>(null);
  useEffect(() => {
    let cancelled = false;
    loadDoughnut().then(cmp => {
      if (!cancelled) setDoughnutCmp(() => cmp);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const hasData = total > 0;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        backgroundColor: theme => theme.brandPalette.background.neutral,
        p: 3
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="labelLarge"
          sx={{
            fontWeight: 700,
            color: theme => theme.brandPalette.foreground.neutral,
            display: 'block'
          }}
        >
          {title}
        </Typography>
        {description != null && (
          <Typography
            variant="labelSmall"
            sx={{
              mt: 0.5,
              color: theme => theme.brandPalette.foreground.neutralFaded,
              display: 'block'
            }}
          >
            {description}
          </Typography>
        )}
      </Box>

      {!hasData ? (
        <Box
          sx={{
            height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            variant="labelSmall"
            sx={{ color: theme => theme.brandPalette.foreground.neutralFaded }}
          >
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          alignItems="center"
          justifyContent="center"
        >
          <Box sx={{ position: 'relative', width: height, height, flexShrink: 0 }}>
            {DoughnutCmp == null ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Loading />
              </Box>
            ) : (
              <DoughnutCmp
                data={{
                  labels: slices.map(s => s.label),
                  datasets: [
                    {
                      data: slices.map(s => s.value),
                      backgroundColor: slices.map(s => s.color),
                      borderColor: 'transparent',
                      borderWidth: 2,
                      hoverOffset: 6
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: '62%',
                  plugins: {
                    legend: { display: false },
                    datalabels: { display: false },
                    tooltip: {
                      callbacks: {
                        label: ctx => {
                          const value = ctx.parsed as number;
                          const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                          return ` ${ctx.label}: ${value.toLocaleString()} (${pct}%)`;
                        }
                      }
                    }
                  }
                }}
              />
            )}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none'
              }}
            >
              <Typography
                variant="labelXSmall"
                sx={{
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontWeight: 600,
                  color: theme => theme.brandPalette.foreground.neutralFaded
                }}
              >
                {totalLabel}
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.75rem',
                  fontWeight: 600,
                  lineHeight: 1.1,
                  color: theme => theme.brandPalette.foreground.neutral
                }}
              >
                {total.toLocaleString()}
              </Typography>
            </Box>
          </Box>

          <Stack spacing={1.25} sx={{ minWidth: 0, flex: 1, width: '100%' }}>
            {slices.map(slice => {
              const pct = total > 0 ? Math.round((slice.value / total) * 100) : 0;
              return (
                <Stack
                  key={slice.label}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ minWidth: 0 }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: slice.color,
                      flexShrink: 0
                    }}
                  />
                  <Typography
                    variant="labelSmall"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      color: theme => theme.brandPalette.foreground.neutral,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {slice.label}
                  </Typography>
                  <Typography
                    variant="labelSmall"
                    sx={{
                      fontWeight: 700,
                      color: theme => theme.brandPalette.foreground.neutral,
                      flexShrink: 0
                    }}
                  >
                    {slice.value.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="labelXSmall"
                    sx={{
                      color: theme => theme.brandPalette.foreground.neutralFaded,
                      width: 36,
                      textAlign: 'right',
                      flexShrink: 0
                    }}
                  >
                    {pct}%
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
